import { useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import Home from '@/pages/Home';
import About from '@/pages/About';
import Catalogue from '@/pages/Catalogue';
import Contact from '@/pages/Contact';
import Policies from '@/pages/Policies';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Catalogue', path: '/catalogue' },
    { name: 'Contact', path: '/contact' },
  ];
  
  const isActive = (path) => location.pathname === path;
  
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-cyan-500/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_nanox-connect/artifacts/v7gkh85r_Gemini_Generated_Image_oay1wpoay1wpoay1.png" 
              alt="M31 NanoX Logo" 
              className="h-12 w-12 rounded-lg transition-transform group-hover:scale-105"
            />
            <div>
              <div className="text-xl font-bold text-white">M31 NanoX</div>
              <div className="text-xs text-cyan-400">Materials for Future Technologies</div>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`text-sm font-medium transition-colors relative group ${
                  isActive(link.path) ? 'text-cyan-400' : 'text-gray-300 hover:text-white'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 transform transition-transform ${
                  isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}></span>
              </Link>
            ))}
          </div>
          
          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
        
        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg transition-colors ${
                  isActive(link.path) 
                    ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-cyan-400' 
                    : 'text-gray-300 hover:bg-slate-800'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}

function Footer() {
  const footerLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Catalogue', path: '/catalogue' },
    { name: 'Contact', path: '/contact' },
    { name: 'Policies', path: '/policies' },
  ];
  
  const locations = [
    { continent: 'North America', cities: 'USA, Canada, Mexico' },
    { continent: 'Europe', cities: 'Germany, UK, France, Netherlands' },
    { continent: 'Asia', cities: 'China, Japan, South Korea, India' },
    { continent: 'Australia', cities: 'Sydney, Melbourne' },
    { continent: 'South America', cities: 'Brazil, Argentina' },
    { continent: 'Africa', cities: 'South Africa, Egypt' },
  ];
  
  return (
    <footer className="bg-slate-950 border-t border-cyan-500/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_nanox-connect/artifacts/v7gkh85r_Gemini_Generated_Image_oay1wpoay1wpoay1.png" 
                alt="M31 NanoX Logo" 
                className="h-10 w-10 rounded-lg"
              />
              <div>
                <div className="text-lg font-bold text-white">M31 NanoX</div>
                <div className="text-xs text-cyan-400">Materials for Future Technologies</div>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Leading provider of advanced nanomaterials and battery materials for next-generation technologies.
            </p>
            <div className="text-gray-400 text-sm">
              <p className="font-semibold text-white mb-2">Contact:</p>
              <p>Email: info@m31nanox.com</p>
              <p className="mt-3 font-semibold text-white">Innovation Hubs:</p>
              <p>Hyderabad, India</p>
              <p>Bengaluru, India</p>
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {footerLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-cyan-400 text-sm transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Global Research Locations */}
          <div>
            <h3 className="text-white font-semibold mb-4">Global Research Clusters</h3>
            <div className="space-y-2 text-sm">
              {locations.map((location, index) => (
                <div key={index} className="text-gray-400">
                  <span className="text-cyan-400 font-semibold">{location.continent}:</span>
                  <span className="ml-2">{location.cities}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} M31 NanoX. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-slate-950">
      <BrowserRouter>
        <Navigation />
        <main className="pt-20">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/catalogue" element={<Catalogue />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/policies" element={<Policies />} />
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;